import type { CurrentUser } from "@cc-consulting-nv/ccsdk";
import type { AuthUser } from "@/types";

export const AUTH_USER_REFRESHED_EVENT = "auth:user-refreshed";

/**
 * Normalize badges into a string array (API can return strings or objects).
 */
function normalizeBadges(rawBadges: unknown): string[] {
  if (!Array.isArray(rawBadges)) return [];

  return rawBadges
    .map((badge) => {
      if (typeof badge === "string") return badge;
      if (badge && typeof badge === "object" && "name" in badge) {
        return String((badge as { name: unknown }).name);
      }
      return null;
    })
    .filter((badge): badge is string => Boolean(badge));
}

/**
 * Determine admin status using either helper method or badge list.
 */
function computeIsAdmin(profile: unknown, badges: string[]): boolean {
  const maybeProfile = profile as { isAdmin?: unknown };

  if (typeof maybeProfile.isAdmin === "function") {
    try {
      return (maybeProfile.isAdmin as () => boolean)();
    } catch (error) {
      console.warn(
        "authUserProfile: profile.isAdmin threw, falling back to badge check",
        error,
      );
    }
  }

  return badges.some((badge) => badge.toLowerCase() === "administrator");
}

function resolveVariantUrl(
  variants: Record<string, unknown> | null | undefined,
  preferredKeys: string[],
): string | undefined {
  if (!variants) {
    return undefined;
  }

  for (const key of preferredKeys) {
    const value = variants[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  return undefined;
}

export function mapCurrentUserToAuthUser(profile: CurrentUser): AuthUser {
  const p = profile as Record<string, unknown>;
  const badges = normalizeBadges(p.badges);
  const isAdmin = computeIsAdmin(profile, badges);
  const avatarVariants = profile.avatarVariants || null;
  const backgroundVariants = profile.backgroundVariants || null;
  const avatarUrl =
    ((p.avatar || p.avatarUrl || p.avatar_url || p.profile_image) as
      | string
      | undefined) ??
    profile.avatarUrl ??
    resolveVariantUrl(avatarVariants as Record<string, unknown> | null, [
      "thumbWebp",
      "thumb",
      "smallWebp",
      "small",
    ]);
  const backgroundImage =
    ((p.backgroundImage || p.background_image) as string | undefined) ??
    resolveVariantUrl(backgroundVariants as Record<string, unknown> | null, [
      "mediumWebp",
      "medium",
      "largeWebp",
      "large",
    ]);
  const background =
    ((p.background ||
      p.backgroundImage ||
      p.background_image ||
      p.coverImage ||
      p.cover_image ||
      p.banner) as string | undefined) ??
    backgroundImage;

  return {
    ulid: profile.ulid,
    username: profile.username || "",
    name: (p.name as string | undefined) || profile.displayName || "",
    displayName: profile.displayName || (p.name as string | undefined) || "",
    email: (p.email as string | undefined) || "",
    avatar: avatarUrl,
    avatarUrl,
    avatarVariants,
    background,
    backgroundImage,
    backgroundVariants,
    bio: profile.bio,
    website: (p.website || p.websiteUrl) as string | undefined,
    followers: profile.followersCount,
    following: profile.followingCount,
    postCount: profile.postsCount,
    verified: p.verified as boolean | undefined,
    badges,
    isAdmin,
    accountSetup: p.accountSetup as boolean | undefined,
    acceptTermsOfService: p.acceptTermsOfService as boolean | undefined,
    referralCode: p.referralCode as string | undefined,
  };
}
